import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CloudSync as EnrollIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  useTable,
  SortingState,
  RowSelectionState,
} from '@tanstack/react-table';
import { pagedFeatures } from '../components/common/tableFeatures';
import errorService from '@services/errorService';
import {
  useGetDpsEnrollmentsQuery,
  useBulkDpsEnrollMutation,
  type DpsEnrollmentDevice,
  type DpsEnrollmentResult,
} from '@store/api/devicesApi';
import { useQueryError } from '@hooks/useQueryError';
import { useDebounce } from '@hooks/useDebounce';
import { useColumnVisibility } from '@hooks/useColumnVisibility';
import ColumnSelector from '@components/common/ColumnSelector';
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
} from '@components/common/DataTable';
import { DPS_ENROLLMENT_COLUMNS } from '@components/dps/dpsEnrollmentColumns';
import { fontFamilyMono } from '../theme/theme';
import {
  DPS_COLUMN_METADATA,
  DPS_COLUMN_PRESETS,
  DPS_DEFAULT_COLUMNS,
} from '@components/dps/dpsColumnMetadata';

const DPSEnrollmentPage: React.FC = () => {
  const [tabValue, setTabValue] = useState<'unenrolled' | 'enrolled' | 'all'>('unenrolled');
  const [error, setError] = useState<string>('');

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const debouncedFilter = useDebounce(globalFilter, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const {
    data: enrollmentData,
    isFetching: loading,
    error: fetchError,
    refetch,
  } = useGetDpsEnrollmentsQuery({ unenrolled: tabValue === 'unenrolled' });
  const [bulkDpsEnroll] = useBulkDpsEnrollMutation();

  const queryError = useQueryError(fetchError);
  const displayError = error || queryError;

  const devices = useMemo<DpsEnrollmentDevice[]>(() => {
    const allDevices = enrollmentData?.data || [];
    if (tabValue === 'enrolled') {
      return allDevices.filter((d) => d.dps_enrollment_status === 'assigned');
    }
    return allDevices;
  }, [enrollmentData, tabValue]);

  const { visibleColumns, toggleColumn, resetToDefault, applyPreset, columnVisibility } =
    useColumnVisibility({
      columns: DPS_COLUMN_METADATA,
      storageKey: 'dpsEnrollment.columnVisibility.v1',
      defaultColumns: DPS_DEFAULT_COLUMNS,
      presets: DPS_COLUMN_PRESETS,
    });

  const table = useTable({
    features: pagedFeatures,
    data: devices,
    columns: DPS_ENROLLMENT_COLUMNS,
    state: {
      sorting,
      rowSelection,
      globalFilter: debouncedFilter,
      columnVisibility,
      pagination: { pageIndex: page, pageSize },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    // Key selection by device_id: that is what the enrollment request submits,
    // so the selection stays correct across sorting and pagination.
    getRowId: (row) => row.device_id,
    // These endpoints return the whole set, so paginate client-side.
  });

  // Off the table, not `rowSelection`: only ids present in the current data,
  // so a selection left over from another tab can never be submitted.
  const selectedDeviceIds = table.getSelectedRowModel().rows.map((row) => row.original.device_id);

  // Enrollment dialog state
  const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
  const [connectionString, setConnectionString] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentResults, setEnrollmentResults] = useState<DpsEnrollmentResult[]>([]);
  const [enrollmentSummary, setEnrollmentSummary] = useState<{
    total: number;
    successful: number;
    failed: number;
    skipped: number;
  } | null>(null);

  const handleTabChange = (value: 'unenrolled' | 'enrolled' | 'all') => {
    setTabValue(value);
    setPage(0);
    setRowSelection({});
  };

  const handleOpenEnrollmentDialog = () => {
    setEnrollmentDialogOpen(true);
    setEnrollmentResults([]);
    setEnrollmentSummary(null);
  };

  const handleCloseEnrollmentDialog = () => {
    setEnrollmentDialogOpen(false);
    setConnectionString('');
    setEnrollmentResults([]);
    setEnrollmentSummary(null);
  };

  const handleEnroll = async () => {
    if (!connectionString) {
      setError('Please provide Connection String');
      return;
    }

    setEnrolling(true);
    setError('');

    try {
      const data = await bulkDpsEnroll({
        connection_string: connectionString,
        device_ids: selectedDeviceIds,
      }).unwrap();

      setEnrollmentResults(data.devices || []);
      setEnrollmentSummary({
        total: data.total,
        successful: data.successful,
        failed: data.failed,
        skipped: data.skipped,
      });

      // The mutation's invalidatesTags refetches the list; just drop the selection.
      setRowSelection({});
    } catch (err) {
      setError(errorService.toUserMessage(err));
    } finally {
      setEnrolling(false);
    }
  };

  const getResultIcon = (result: DpsEnrollmentResult) => {
    if (result.success) {
      return <SuccessIcon color="success" />;
    } else if (result.status === 'skipped') {
      return <WarningIcon color="warning" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const selectedCount = selectedDeviceIds.length;
  const emptyMessage =
    tabValue === 'unenrolled'
      ? 'No unenrolled devices found'
      : tabValue === 'enrolled'
        ? 'No enrolled devices found'
        : 'No devices found';

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Azure DPS Enrollment</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<EnrollIcon />}
            onClick={handleOpenEnrollmentDialog}
            disabled={selectedCount === 0}
          >
            Enroll Selected ({selectedCount})
          </Button>
        </Box>
      </Box>

      {displayError && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {displayError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Azure Device Provisioning Service (DPS)</strong> enables zero-touch device
          provisioning to IoT Hub. Devices must be enrolled in DPS before they can connect to Azure
          IoT Hub. This page shows enrollment status and allows bulk enrollment operations.
        </Typography>
      </Alert>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, value) => handleTabChange(value)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            px: 1,
            '& .MuiTab-root': {
              minHeight: 40,
              py: 0,
              textTransform: 'none',
              fontSize: '0.875rem',
            },
          }}
        >
          <Tab label="Unenrolled Devices" value="unenrolled" />
          <Tab label="Enrolled Devices" value="enrolled" />
          <Tab label="All Devices" value="all" />
        </Tabs>

        <DataTableToolbar
          selectedCount={selectedCount}
          searchValue={globalFilter}
          onSearchChange={(value) => {
            setGlobalFilter(value);
            setPage(0);
          }}
          searchPlaceholder="Search devices..."
          searchAriaLabel="Search devices"
          actions={
            <ColumnSelector
              columns={DPS_COLUMN_METADATA}
              visibleColumns={visibleColumns}
              onColumnToggle={toggleColumn}
              onReset={resetToDefault}
              onPresetSelect={applyPreset}
              presets={DPS_COLUMN_PRESETS}
            />
          }
        />

        <DataTable
          table={table}
          ariaLabel="DPS enrollment"
          isLoading={loading}
          emptyMessage={emptyMessage}
        />

        <DataTablePagination
          count={table.getFilteredRowModel().rows.length}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </Paper>

      {/* Enrollment Dialog */}
      <Dialog
        open={enrollmentDialogOpen}
        onClose={handleCloseEnrollmentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Enroll Devices in Azure DPS
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            {selectedCount} device{selectedCount === 1 ? '' : 's'} selected
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                Provide the DPS Shared Access Policy connection string, requested from Team
                Rainbow (valid for about one day). Its host determines which DPS environment
                the devices are enrolled into. The connection string will not be stored and is
                only used for this enrollment operation.
              </Typography>
            </Alert>

            <TextField
              label="DPS Connection String"
              value={connectionString}
              onChange={(e) => setConnectionString(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="HostName=xxx.azure-devices-provisioning.net;SharedAccessKeyName=xxx;SharedAccessKey=xxx"
              disabled={enrolling || enrollmentResults.length > 0}
              helperText="Shared Access Policy connection string with enrollment permissions"
            />

            {enrolling && (
              <Box>
                <LinearProgress />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1
                  }}>
                  Enrolling devices in Azure DPS...
                </Typography>
              </Box>
            )}

            {enrollmentSummary && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Enrollment Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Total: ${enrollmentSummary.total}`} color="default" />
                  <Chip label={`Successful: ${enrollmentSummary.successful}`} color="success" />
                  {enrollmentSummary.failed > 0 && (
                    <Chip label={`Failed: ${enrollmentSummary.failed}`} color="error" />
                  )}
                  {enrollmentSummary.skipped > 0 && (
                    <Chip label={`Skipped: ${enrollmentSummary.skipped}`} color="warning" />
                  )}
                </Box>
              </Paper>
            )}

            {enrollmentResults.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead
                    sx={{
                      bgcolor: 'action.hover',
                      '& .MuiTableCell-head': {
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        letterSpacing: 0,
                        py: 1,
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell width={50} />
                      <TableCell>Device ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrollmentResults.map((result) => (
                      <TableRow key={result.device_id}>
                        <TableCell>{getResultIcon(result)}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: fontFamilyMono, fontSize: '0.75rem' }}
                          >
                            {result.device_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={result.status || (result.success ? 'assigned' : 'failed')}
                            color={
                              result.success
                                ? 'success'
                                : result.status === 'skipped'
                                  ? 'warning'
                                  : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {result.error && (
                            <Typography variant="body2" color="error">
                              {result.error}
                            </Typography>
                          )}
                          {result.assigned_hub && (
                            <Typography variant="body2" sx={{
                              color: 'text.secondary'
                            }}>
                              Hub: {result.assigned_hub}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEnrollmentDialog} disabled={enrolling}>
            {enrollmentResults.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {!enrollmentResults.length && (
            <Button
              onClick={handleEnroll}
              variant="contained"
              disabled={enrolling || !connectionString || selectedCount === 0}
            >
              Enroll Devices
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DPSEnrollmentPage;
